// 재고 RPC 에러 분류(순수 함수, 의존성 없음). hook과 component가 함께 쓰므로 service가 아닌 leaf
// util로 둔다(component→service 경계 위반 회피 — deposit/depositErrors.ts와 동일 이유).
// types/inventory와 같은 도메인 leaf 취급.

// create_inventory_item_rpc / record_inventory_movement_rpc 공통: 미인증 세션(RLS auth.uid() null)
// 시 ERRCODE 'P0010'으로 RAISE.
export function isUnauthenticatedInventoryError(err: unknown): boolean {
  const e = err as { code?: string; message?: string } | null;
  return !!e && e.code === 'P0010';
}

// create_inventory_item_rpc: 같은 유저의 활성(미삭제) 동명(정규화 기준) 상품이 이미 있으면 거부.
// 직접 UPDATE 경로(상품 정보 수정)의 raw 23505는 여기서 흡수하지 않는다 — 이 predicate는 전
// 액션 공유 매퍼가 쓰므로 SQLSTATE 블랭킷 매칭은 무관 유니크 위반까지 '이미 등록된 상품'으로
// 오분류할 수 있다(codexloop R1 #3). 번역은 repo.updateItem이 자기 스코프에서 수행(그 UPDATE가
// 건드릴 수 있는 유니크 제약은 uq_inventory_item_user_norm뿐이라 23505 = 동명 충돌 확정).
export function isDuplicateInventoryItemError(err: unknown): boolean {
  const e = err as { code?: string; message?: string } | null;
  return !!e && /duplicate_inventory_item/i.test(e.message ?? '');
}

// create_inventory_item_rpc: 정규화 후 빈 이름 등 상품명 자체가 유효하지 않음.
export function isInvalidItemNameError(err: unknown): boolean {
  const e = err as { code?: string; message?: string } | null;
  return !!e && /invalid_item_name/i.test(e.message ?? '');
}

// create_inventory_item_rpc / record_inventory_movement_rpc 공통: 수량·모드 등 인자 검증 실패.
export function isInvalidInventoryArgumentError(err: unknown): boolean {
  const e = err as { code?: string; message?: string } | null;
  return !!e && /invalid_argument/i.test(e.message ?? '');
}

// record_inventory_movement_rpc: 대상 상품이 삭제됐거나 타 유저 소유라 안 보임(ERRCODE 'P0012').
export function isInventoryItemNotFoundError(err: unknown): boolean {
  const e = err as { code?: string; message?: string } | null;
  return !!e && (e.code === 'P0012' || /inventory_item_not_found/i.test(e.message ?? ''));
}

// bulk_upsert_inventory_items_rpc: 서버 최후 방어선(캡 초과/파일 내 중복/값 오류/빈 배열/모드
// 오류). 정상 흐름에서는 T3 모달이 미리보기 단계에서 전부 클라 차단하므로 실사용 도달 경로는
// 레이스(같은 세션에서 파일 재선택 없이 재시도 등) 방어용이다 — duplicate_inventory_item(동명
// 활성 아이템 unique 충돌)은 isDuplicateInventoryItemError가 이미 커버하므로 여기 포함하지 않는다.
export function isBulkUpsertValidationError(err: unknown): boolean {
  const e = err as { code?: string; message?: string } | null;
  return (
    !!e &&
    /empty_item_name|invalid_value|too_many_rows|duplicate_item_name_in_file|empty_items|invalid_items_payload|invalid_mode/i.test(
      e.message ?? '',
    )
  );
}
